(function () {
  'use strict';

  const CONFIG = {
    widgetSelector: 'iframe',
    fullWeekendVariant: '54220587041136',
    oneDayVariant: '54220587008368',
    maxSelections: 20
  };

  /*
    Safari fix: month names are looked up here instead of being
    parsed with new Date("October 2026 1"), which Safari rejects.
    Icelandic names are included so it also works after translation.
  */
  const MONTH_INDEX = {
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
    'janúar': 0, 'febrúar': 1, mars: 2, 'apríl': 3, 'maí': 4, 'júní': 5,
    'júlí': 6, 'ágúst': 7, 'október': 9, 'nóvember': 10, desember: 11
  };

  /* Button labels in English and Icelandic */
  const WEEKEND_LABELS = ['Full Weekend', 'Heil helgi'];
  const DAY_LABELS = ['1 Day', '1 dagur'];

  function isWeekendText(text) {
    return WEEKEND_LABELS.indexOf(text) !== -1;
  }

  function isDayText(text) {
    return DAY_LABELS.indexOf(text) !== -1;
  }

  function makeUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return (
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2) +
      Math.random().toString(36).slice(2)
    );
  }

  let frame = null;
  let doc = null;
  let state = {
    mode: null,
    saved: [],
    adding: false
  };

  function getFrame() {
    const frames = document.querySelectorAll(CONFIG.widgetSelector);

    for (const iframe of frames) {
      try {
        const d = iframe.contentDocument;

        if (
          d &&
          d.querySelector('.rental') &&
          d.querySelector('button[name="day"]')
        ) {
          return { iframe, doc: d };
        }
      } catch (e) {}
    }

    return null;
  }

  function getMode() {
    if (!doc) return null;

    const buttons = [...doc.querySelectorAll('[data-testid="variant-select"] button')];

    const active = buttons.find(button => {
      const style = window.getComputedStyle(button);
      return (
        button.classList.contains('active') ||
        button.getAttribute('aria-pressed') === 'true' ||
        style.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
        style.backgroundColor !== 'transparent'
      );
    });

    if (active) {
      const text = active.textContent.trim();

      if (isWeekendText(text)) return 'weekend';
      if (isDayText(text)) return 'day';
    }

    /*
      Fallback:
      BTA initially normally has the first option selected.
    */
    const weekendButton = buttons.find(
      b => isWeekendText(b.textContent.trim())
    );

    if (
      weekendButton &&
      weekendButton.className.includes('active')
    ) {
      return 'weekend';
    }

    return state.mode || 'day';
  }

  function getCalendarMonth() {
    if (!doc) return null;

    const caption = doc.querySelector('.rdp-caption_label');

    if (!caption) return null;

    /* "October 2026" or "október 2026" */
    const parts = caption.textContent.trim().toLowerCase().split(/\s+/);

    const month = MONTH_INDEX[parts[0]];
    const year = parseInt(parts[1], 10);

    if (month === undefined || Number.isNaN(year)) return null;

    return {
      month: month,
      year: year
    };
  }

  function getButtonDate(button) {
    const calendar = button.closest('.rdp-month');

    if (!calendar) return null;

    const monthInfo = getCalendarMonth();

    if (!monthInfo) return null;

    const day = parseInt(button.textContent.trim(), 10);

    if (!day) return null;

    let month = monthInfo.month;
    let year = monthInfo.year;

    /*
      BTA/React Day Picker displays previous/next month
      days with rdp-day_outside.
    */
    if (button.classList.contains('rdp-day_outside')) {
      const row = button.closest('tr');

      if (!row) return null;

      const buttonsInRow = [...row.querySelectorAll('button[name="day"]')];

      const index = buttonsInRow.indexOf(button);

      /*
        If an outside date is at the beginning of the calendar,
        it belongs to previous month.
      */
      if (index <= 2) {
        month--;

        if (month < 0) {
          month = 11;
          year--;
        }
      } else {
        /*
          Outside dates at the end belong to next month.
        */
        month++;

        if (month > 11) {
          month = 0;
          year++;
        }
      }
    }

    return {
      day,
      month,
      year
    };
  }

  function formatDate(date) {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return `${monthNames[date.month]} ${date.day}, ${date.year}`;
  }

  function isoDate(date) {
    return [
      date.year,
      String(date.month + 1).padStart(2, '0'),
      String(date.day).padStart(2, '0')
    ].join('-');
  }

  function usDate(date) {
    return [
      String(date.month + 1).padStart(2, '0'),
      String(date.day).padStart(2, '0'),
      date.year
    ].join('/');
  }

  function getCurrentSelection() {
    if (!doc) return null;

    const selected = [
      ...doc.querySelectorAll(
        'button[name="day"].rdp-day_selected:not(.rdp-day_disabled)'
      )
    ];

    if (!selected.length) return null;

    const dates = selected
      .map(button => getButtonDate(button))
      .filter(Boolean)
      .sort(
        (a, b) =>
          new Date(a.year, a.month, a.day) -
          new Date(b.year, b.month, b.day)
      );

    if (!dates.length) return null;

    const mode = getMode();

    if (mode === 'weekend') {
      return {
        type: 'weekend',
        start: dates[0],
        end: dates[dates.length - 1]
      };
    }

    return {
      type: 'day',
      start: dates[0],
      end: dates[0]
    };
  }

  function selectionKey(selection) {
    return `${isoDate(selection.start)}_${isoDate(selection.end)}`;
  }

  function isAlreadySaved(selection) {
    return state.saved.some(
      item => selectionKey(item) === selectionKey(selection)
    );
  }

  function renderUI() {
    if (!doc) return;

    let box = doc.querySelector('#multi-booking-box');

    if (!box) {
      box = doc.createElement('div');
      box.id = 'multi-booking-box';

      box.style.marginTop = '18px';
      box.style.padding = '14px';
      box.style.border = '1px solid #ddd';
      box.style.borderRadius = '6px';
      box.style.background = '#fff';

      const calendar = doc.querySelector('.rental');

      if (calendar) {
        calendar.parentNode.insertBefore(box, calendar.nextSibling);
      }
    }

    const mode = getMode();

    const title =
      mode === 'weekend'
        ? 'Selected weekends'
        : 'Selected dates';

    let html = `
      <div style="
        font-weight:600;
        font-size:16px;
        margin-bottom:10px;
      ">
        ${title}
      </div>
    `;

    if (!state.saved.length) {
      html += `
        <div style="
          font-size:14px;
          color:#777;
          margin-bottom:10px;
        ">
          No selections added yet.
        </div>
      `;
    } else {
      html += `<div>`;

      state.saved.forEach((item, index) => {
        if (item.type === 'weekend') {
          html += `
            <div style="
              display:flex;
              align-items:center;
              justify-content:space-between;
              gap:10px;
              margin:6px 0;
              font-size:14px;
            ">
              <span>
                ${index + 1}. ${formatDate(item.start)}
                → ${formatDate(item.end)}
              </span>

              <button
                type="button"
                data-remove-index="${index}"
                style="
                  border:0;
                  background:none;
                  cursor:pointer;
                  color:#c00;
                  font-weight:600;
                "
              >
                Remove
              </button>
            </div>
          `;
        } else {
          html += `
            <div style="
              display:flex;
              align-items:center;
              justify-content:space-between;
              gap:10px;
              margin:6px 0;
              font-size:14px;
            ">
              <span>
                ${index + 1}. ${formatDate(item.start)}
              </span>

              <button
                type="button"
                data-remove-index="${index}"
                style="
                  border:0;
                  background:none;
                  cursor:pointer;
                  color:#c00;
                  font-weight:600;
                "
              >
                Remove
              </button>
            </div>
          `;
        }
      });

      html += `</div>`;
    }

    html += `
      <button
        type="button"
        id="multi-add-button"
        style="
          width:100%;
          margin-top:10px;
          padding:11px 14px;
          border:0;
          border-radius:5px;
          background:#ff5a36;
          color:#fff;
          cursor:pointer;
          font-size:15px;
          font-weight:600;
        "
      >
        + Add Another ${mode === 'weekend' ? 'Weekend' : 'Date'}
      </button>
    `;

    box.innerHTML = html;

    const addButton = box.querySelector('#multi-add-button');

    if (addButton) {
      addButton.onclick = function () {
        const current = getCurrentSelection();

        if (!current) {
          alert(
            mode === 'weekend'
              ? 'Please select a weekend first.'
              : 'Please select a date first.'
          );

          return;
        }

        if (!isAlreadySaved(current)) {
          state.saved.push(current);
        }

        if (state.saved.length >= CONFIG.maxSelections) {
          addButton.disabled = true;
        }

        state.adding = true;

        renderUI();

        console.log(
          '[Multi Booking] Saved:',
          current
        );
      };
    }

    box.querySelectorAll('[data-remove-index]').forEach(button => {
      button.onclick = function () {
        const index = Number(
          button.getAttribute('data-remove-index')
        );

        state.saved.splice(index, 1);

        renderUI();
      };
    });
  }

  function attachCalendarListener() {
    if (!doc || doc.__multiBookingAttached) return;

    doc.__multiBookingAttached = true;

    doc.addEventListener(
      'click',
      function (event) {
        const button = event.target.closest(
          'button[name="day"]'
        );

        if (!button) return;

        if (button.disabled) return;

        /*
          If user pressed "Add Another", save the old selection
          BEFORE BTA replaces it with the new selection.
        */
        if (state.adding) {
          const oldSelection = getCurrentSelection();

          if (
            oldSelection &&
            !isAlreadySaved(oldSelection)
          ) {
            state.saved.push(oldSelection);
          }

          state.adding = false;

          setTimeout(() => {
            renderUI();
          }, 50);

          return;
        }

        /*
          Normal BTA selection.
        */
        setTimeout(() => {
          renderUI();
        }, 100);
      },
      true
    );
  }

  function attachVariantListener() {
    if (!doc || doc.__multiVariantAttached) return;

    doc.__multiVariantAttached = true;

    doc.addEventListener(
      'click',
      function (event) {
        const button = event.target.closest(
          '[data-testid="variant-select"] button'
        );

        if (!button) return;

        const text = button.textContent.trim();

        if (isDayText(text)) {
          state.mode = 'day';
        }

        if (isWeekendText(text)) {
          state.mode = 'weekend';
        }

        /*
          Switching duration clears previously saved selections
          because the variant has changed.
        */
        state.saved = [];
        state.adding = false;

        setTimeout(() => {
          renderUI();
        }, 150);
      },
      true
    );
  }

  function attachBookButton() {
    if (!doc || doc.__multiBookAttached) return;

    doc.__multiBookAttached = true;

    doc.addEventListener(
      'click',
      async function (event) {
        const button = event.target.closest(
          'button[data-type="submit"]'
        );

        if (!button) return;

        /*
          If there are no saved selections, let normal BTA
          behavior continue.
        */
        if (!state.saved.length) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const mode = getMode();

        /*
          Include the currently selected date/range too.
        */
        const current = getCurrentSelection();

        const allSelections = [...state.saved];

        if (
          current &&
          !isAlreadySaved(current)
        ) {
          allSelections.push(current);
        }

        if (!allSelections.length) {
          alert('Please select at least one date.');
          return;
        }

        console.log(
          '[Multi Booking] Final selections:',
          allSelections
        );

        const variantId =
          mode === 'weekend'
            ? CONFIG.fullWeekendVariant
            : CONFIG.oneDayVariant;

        const items = allSelections.map(selection => {
          const start = selection.start;

          return {
            id: Number(variantId),
            quantity: 1,
            properties: {
              Start: usDate(start),
              _start_iso8601:
                `${isoDate(start)}T00:00:00.000Z`,
              _addons_uuid: makeUuid()
            }
          };
        });

        const originalLabel = button.textContent;

        button.disabled = true;
        button.textContent = 'Adding...';

        try {
          const response = await fetch(
            '/cart/add.js',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items
              })
            }
          );

          if (!response.ok) {
            throw new Error(
              'Unable to add booking to cart.'
            );
          }

          await response.json();

          window.location.href = window.Shopify.routes.root + 'cart';

        } catch (error) {
          console.error(
            '[Multi Booking] Cart error:',
            error
          );

          alert(
            'Something went wrong while adding your booking. Please try again.'
          );

          button.disabled = false;
          button.textContent = originalLabel;
        }
      },
      true
    );
  }

  function init() {
    const result = getFrame();

    if (!result) {
      setTimeout(init, 500);
      return;
    }

    frame = result.iframe;
    doc = result.doc;

    state.mode = getMode();

    attachCalendarListener();
    attachVariantListener();
    attachBookButton();

    renderUI();

    console.log(
      '[Multi Booking] BTA multi-date system loaded.'
    );
  }

  /*
    BTA iframe is created dynamically, so keep checking until
    it becomes available.
  */
  init();

})();